import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sortVariants } from '../lib/sortVariants';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import PaginationBar from './PaginationBar';
import MultiSelectFilter from './MultiSelectFilter';
import SortDropdown from './SortDropdown';
import api from '../lib/api';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0].slice(-2)}`;
  return d;
};

const TALLY_STATUSES = {
  pending: { label: 'Bill Pending', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  billed: { label: 'Billed', color: 'bg-green-100 text-green-700 border-green-300' },
  hold_admin: { label: 'Admin Hold', color: 'bg-red-100 text-red-700 border-red-300' },
  hold_sales: { label: 'Sales Hold', color: 'bg-orange-100 text-orange-700 border-orange-300' },
};

const SalesEntryManagement = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const bulk = useBulkSelect();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [expandedSE, setExpandedSE] = useState(null);
  const [billingDialog, setBillingDialog] = useState(null); // {entryId, type: 'SE', inputValue: ''}

  // Filters
  const [filterCustomers, setFilterCustomers] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterTallyStatuses, setFilterTallyStatuses] = useState([]);
  const [filterEntryTypes, setFilterEntryTypes] = useState([]);
  const [filterOrderSources, setFilterOrderSources] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterAgents, setFilterAgents] = useState([]);
  const [filterDistributors, setFilterDistributors] = useState([]);
  const [filterPaymentModes, setFilterPaymentModes] = useState([]);
  const [filterStaff, setFilterStaff] = useState([]);
  const [searchSE, setSearchSE] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 200;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const searchTimerRef = useRef(null);

  // Share dialog
  const [shareDialogEntry, setShareDialogEntry] = useState(null);
  const [shareFormat, setShareFormat] = useState('pdf');
  const [hideColor, setHideColor] = useState(false);

  // Entry creation
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [entryType, setEntryType] = useState('direct_sale');
  const [transportName, setTransportName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [entryItems, setEntryItems] = useState([]);
  const [entryNotes, setEntryNotes] = useState('');
  const [orderSource, setOrderSource] = useState('');
  const [pendingSOItems, setPendingSOItems] = useState([]);
  const [autoDetected, setAutoDetected] = useState([]);  // [{entryItemIdx, variant, so_number, so_id, item_id}]
  const [scanInput, setScanInput] = useState('');
  const scanRef = useRef(null);

  // Keyboard shortcuts: Ctrl+S to save, Esc to close dialogs
  useKeyboardShortcuts({
    onSave: () => { if (isCreateOpen) submitEntryRef.current?.(); },
    onEscape: () => { if (viewingEntry) setViewingEntry(null); else if (shareDialogEntry) setShareDialogEntry(null); },
    onNew: () => { if (!isCreateOpen) setIsCreateOpen(true); },
  });
  const submitEntryRef = useRef(null);

  // Substitution state
  const [substituteIdx, setSubstituteIdx] = useState(null); // index of item being substituted
  const [similarProducts, setSimilarProducts] = useState([]);
  const [substituteSearch, setSubstituteSearch] = useState('');
  const [showSubstituteDialog, setShowSubstituteDialog] = useState(false);

  useEffect(() => { loadData(); }, []);

  // Pre-fill from Ready to Dispatch navigation
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefillDone.current || loading || !location.state?.fromDispatch) return;
    const dispatch = location.state;
    prefillDone.current = true;

    // Open the create dialog and pre-fill
    setSelectedCustomer(dispatch.customerId || '');
    setEntryType('against_so');

    // Build entry items from dispatch data
    const newItems = [];
    for (const dItem of dispatch.items || []) {
      const product = products.find(p => p.id === dItem.product_id);
      if (!product) continue;
      const manualQty = {};
      for (const v of dItem.variants || []) {
        manualQty[`${v.color}_${v.size}`] = v.qty;
      }
      newItems.push({
        product_id: product.id,
        design_code: product.design_code || dItem.design_code,
        brand_id: product.brand_id || '',
        fabric: product.fabric || '',
        images: product.images || [],
        manualQty,
      });
    }
    setEntryItems(newItems);

    // Build auto-detected SO links
    const autoLinks = [];
    for (let idx = 0; idx < (dispatch.items || []).length; idx++) {
      const dItem = dispatch.items[idx];
      for (const v of dItem.variants || []) {
        autoLinks.push({
          entryItemIdx: idx,
          color: v.color,
          size: v.size,
          so_id: dispatch.orderId,
          so_number: dispatch.soNumber,
          item_id: dItem.item_id,
          qty_pending: v.qty,
        });
      }
    }
    setAutoDetected(autoLinks);
    setPendingSOItems([{ so_id: dispatch.orderId, so_number: dispatch.soNumber }]);

    setIsCreateOpen(true);
    toast.success(`Pre-filled from ${dispatch.soNumber} — review and submit`);
    // Clear navigation state to prevent re-trigger on refresh
    window.history.replaceState({}, document.title);
  }, [loading, location.state, products]);

  const loadData = async () => {
    try {
      const [entriesRes, custRes, prodRes, brandsRes, agentsRes, distRes] = await Promise.allSettled([
        api.get('/sales-entries'), api.get('/customers?include_inactive=true'), api.get('/products'), api.get('/brands'),
        api.get('/agents'), api.get('/distributors'),
      ]);
      if (entriesRes.status === 'fulfilled') {
        setEntries(entriesRes.value.data);
        setTotalCount(parseInt(entriesRes.value.headers?.['x-total-count'] || entriesRes.value.data.length));
        setCurrentPage(1);
      }
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);
      if (prodRes.status === 'fulfilled') setProducts(prodRes.value.data);
      if (brandsRes.status === 'fulfilled') setBrands(brandsRes.value.data);
      if (agentsRes.status === 'fulfilled') setAgents(agentsRes.value.data);
      if (distRes.status === 'fulfilled') setDistributors(distRes.value.data);
      api.get('/drafts?type=SE').then(r => setDrafts(r.data || [])).catch(() => {});
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const loadFilteredEntries = async (page = 1, searchTerm = null) => {
    try {
      const params = new URLSearchParams();
      if (filterCustomers.length) params.append('customer_id', filterCustomers.join(','));
      if (filterStatuses.length) params.append('status', filterStatuses.join(','));
      if (filterTallyStatuses.length) params.append('tally_status', filterTallyStatuses.join(','));
      if (filterEntryTypes.length) params.append('entry_type', filterEntryTypes[0]);
      if (filterOrderSources.length) params.append('order_source', filterOrderSources.join(','));
      if (filterCities.length) params.append('city', filterCities.join(','));
      if (filterStates.length) params.append('state', filterStates.join(','));
      if (filterAgents.length) params.append('agent_id', filterAgents.join(','));
      if (filterDistributors.length) params.append('distributor_id', filterDistributors.join(','));
      if (filterPaymentModes.length) params.append('payment_mode', filterPaymentModes.join(','));
      const q = searchTerm !== null ? searchTerm : searchSE;
      if (q && q.trim()) params.append('search', q.trim());
      params.append('page', page);
      params.append('limit', PAGE_SIZE);
      const res = await api.get(`/sales-entries?${params.toString()}`);
      setEntries(res.data);
      setTotalCount(parseInt(res.headers?.['x-total-count'] || res.data.length));
      setCurrentPage(page);
    } catch { toast.error('Failed to filter'); }
  };

  const goToPage = (p) => { if (p >= 1 && p <= totalPages) loadFilteredEntries(p); };

  const handleSearchChange = (value) => {
    setSearchSE(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadFilteredEntries(1, value), 400);
  };

  useEffect(() => { if (!loading) { setCurrentPage(1); loadFilteredEntries(1); } }, [filterCustomers, filterStatuses, filterTallyStatuses, filterEntryTypes, filterOrderSources, filterCities, filterStates, filterAgents, filterDistributors, filterPaymentModes]);

  const getCustName = (id) => {
    const c = customers.find(c => c.id === id);
    if (!c) return 'Unknown';
    const name = c.company_name || 'Unknown';
    const loc = c.city?.toLowerCase() === 'mumbai' ? c.area : c.city;
    return loc ? `${name} (${loc})` : name;
  };
  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';
  const getAgentName = (custId) => { const c = customers.find(c => c.id === custId); return c?.agent_id ? agents.find(a => a.id === c.agent_id)?.name : ''; };
  const getDistName = (custId) => { const c = customers.find(c => c.id === custId); return c?.distributor_id ? distributors.find(d => d.id === c.distributor_id)?.company_name : ''; };
  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff' || user?.role === 'salesman';

  const updateTallyStatus = async (entryId, newStatus, invoiceNumber) => {
    try {
      const payload = { tally_status: newStatus };
      if (invoiceNumber) payload.invoice_number = invoiceNumber;
      await api.put(`/sales-entries/${entryId}/tally-status`, payload);
      toast.success(`Status updated to ${TALLY_STATUSES[newStatus]?.label || newStatus}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleStatusChange = (entryId, newStatus) => {
    if (newStatus === 'billed') {
      setBillingDialog({ entryId, type: 'SE', inputValue: '' });
    } else {
      updateTallyStatus(entryId, newStatus);
    }
  };

  const confirmBilling = () => {
    if (!billingDialog) return;
    updateTallyStatus(billingDialog.entryId, 'billed', billingDialog.inputValue || null);
    setBillingDialog(null);
  };

  const canChangeTally = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || user?.role === 'inventory_staff';
  const canSetAdminHold = user?.role === 'admin' || user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Edit entry (admin only) — navigate to full edit form
  const openEditEntry = (entry) => {
    navigate('/create-sales-entry', { state: { editSE: entry } });
  };

  // Delete entry (admin only)
  const deleteEntry = async (entryId, entryNumber) => {
    try {
      await api.delete(`/sales-entries/${entryId}`);
      toast.success(`${entryNumber} deleted! Stock restored.`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  // Share function
  const executeShare = async (entry, format, action) => {
    if (action === 'download') {
      try {
        let endpoint = format === 'pdf' ? `/sales-entries/${entry.id}/pdf` : `/sales-entries/${entry.id}/excel`;
        if (format === 'pdf' && hideColor) endpoint += '?hide_color=true';
        const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const ext = format === 'pdf' ? '.pdf' : '.xlsx';
        const res = await api.get(endpoint, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
        const a = document.createElement('a'); a.href = url; a.download = `${entry.entry_number}${ext}`; a.click();
        window.URL.revokeObjectURL(url); toast.success(`${format.toUpperCase()} downloaded!`);
      } catch { toast.error(`${format.toUpperCase()} generation failed`); }
    } else if (action === 'whatsapp') {
      const cust = customers.find(c => c.id === entry.customer_id);
      const msg = `Packing Slip ${entry.entry_number}\nCustomer: ${cust?.company_name || 'N/A'}\nDate: ${fmtDate(entry.entry_date)}\nItems: ${entry.items?.length || 0} designs\nQty: ${entry.total_qty || 0} pcs\nTotal: ₹${(entry.total_amount || 0).toLocaleString('en-IN')}\n\nFrom Jini Nimi Fashion LLP`;
      window.open(`https://wa.me/${cust?.phone || ''}?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (action === 'email') {
      const cust = customers.find(c => c.id === entry.customer_id);
      const subject = `Packing Slip ${entry.entry_number} - Jini Nimi Fashion LLP`;
      const body = `Dear ${cust?.contact_person || 'Sir/Madam'},\n\nEntry Number: ${entry.entry_number}\nDate: ${fmtDate(entry.entry_date)}\nItems: ${entry.items?.length || 0} designs\nQty: ${entry.total_qty || 0} pcs\nTotal: ₹${(entry.total_amount || 0).toLocaleString('en-IN')}\n\nRegards,\nJini Nimi Fashion LLP`;
      window.open(`mailto:${cust?.email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_self');
    }
    setShareDialogEntry(null);
  };

  // Filter options
  const custFilterOptions = customers.map(c => ({ value: c.id, label: c.company_name }));
  const statusFilterOptions = [{ value: 'packed', label: 'Packed' }, { value: 'dispatched', label: 'Dispatched' }];
  const tallyFilterOptions = [
    { value: 'pending', label: 'Bill Pending' }, { value: 'billed', label: 'Billed' },
    { value: 'hold_admin', label: 'Admin Hold' }, { value: 'hold_sales', label: 'Sales Hold' },
  ];
  const entryTypeOptions = [{ value: 'direct_sale', label: 'Direct Sale' }, { value: 'against_so', label: 'Against SO' }];
  const orderSourceOptions = [
    { value: 'whatsapp', label: 'WhatsApp' }, { value: 'walk_in', label: 'Walk-in' },
    { value: 'instagram', label: 'Instagram' }, { value: 'facebook', label: 'Facebook' },
    { value: 'exhibition_followup', label: 'Exhibition Follow-up' }, { value: 'word_of_mouth', label: 'Word of Mouth' },
    { value: 'agent_referral', label: 'Agent Referral' }, { value: 'distributor_referral', label: 'Distributor Referral' },
    { value: 'repeat_customer', label: 'Repeat Customer' }, { value: 'other', label: 'Other' },
  ];
  const agentFilterOptions = agents.map(a => ({ value: a.id, label: a.name }));
  const distFilterOptions = distributors.map(d => ({ value: d.id, label: d.name || d.company_name }));
  const cityFilterOptions = [...new Set(customers.filter(c => !filterStates.length || filterStates.includes(c.state)).map(c => c.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const stateFilterOptions = [...new Set(customers.map(c => c.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const staffFilterOptions = [...new Set(entries.map(e => e.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));
  const hasAnyFilter = filterCustomers.length > 0 || filterStatuses.length > 0 || filterTallyStatuses.length > 0 || filterEntryTypes.length > 0 || filterOrderSources.length > 0 || filterCities.length > 0 || filterStates.length > 0 || filterAgents.length > 0 || filterDistributors.length > 0 || filterPaymentModes.length > 0 || filterStaff.length > 0 || searchSE;

  const seSortOptions = [
    { value: 'date_desc', label: 'Newest First' },
    { value: 'date_asc', label: 'Oldest First' },
    { value: 'name_asc', label: 'A to Z (Customer)' },
    { value: 'name_desc', label: 'Z to A (Customer)' },
    { value: 'qty_desc', label: 'Qty: High to Low' },
    { value: 'qty_asc', label: 'Qty: Low to High' },
    { value: 'amount_desc', label: 'Amount: High to Low' },
    { value: 'amount_asc', label: 'Amount: Low to High' },
  ];

  const filteredEntries = useMemo(() => {
    let list = searchSE
      ? entries.filter(e => {
          const q = searchSE.toLowerCase();
          const matchesName = getCustName(e.customer_id)?.toLowerCase().includes(q);
          const matchesNumber = /\d/.test(searchSE) && e.entry_number?.toLowerCase().includes(q);
          const matchesDesign = (e.items || []).some(item => item.design_code?.toLowerCase().includes(q));
          return matchesName || matchesNumber || matchesDesign;
        })
      : [...entries];
    if (filterStaff.length) list = list.filter(e => filterStaff.includes(e.created_by_name));
    list.sort((a, b) => {
      const cA = getCustName(a.customer_id), cB = getCustName(b.customer_id);
      switch (sortBy) {
        case 'date_asc': return new Date(a.entry_date || a.created_at) - new Date(b.entry_date || b.created_at);
        case 'name_asc': return cA.localeCompare(cB);
        case 'name_desc': return cB.localeCompare(cA);
        case 'qty_desc': return (b.total_qty || 0) - (a.total_qty || 0);
        case 'qty_asc': return (a.total_qty || 0) - (b.total_qty || 0);
        case 'amount_desc': return (b.total_amount || 0) - (a.total_amount || 0);
        case 'amount_asc': return (a.total_amount || 0) - (b.total_amount || 0);
        default: return new Date(b.entry_date || b.created_at) - new Date(a.entry_date || a.created_at);
      }
    });
    return list;
  }, [entries, searchSE, sortBy]);

  // Substitution helpers
  const openSubstituteDialog = async (idx) => {
    const item = entryItems[idx];
    setSubstituteIdx(idx);
    setSubstituteSearch('');
    try {
      const res = await api.get(`/products/${item.product_id}/similar`);
      setSimilarProducts(res.data);
    } catch { setSimilarProducts([]); }
    setShowSubstituteDialog(true);
  };

  const confirmSubstitution = (newProduct) => {
    if (substituteIdx === null) return;
    setEntryItems(prev => prev.map((item, i) => {
      if (i !== substituteIdx) return item;
      return {
        ...item,
        original_product_id: item.original_product_id || item.product_id,
        original_design_code: item.original_design_code || item.design_code,
        product_id: newProduct.id,
        design_code: newProduct.design_code,
        brand_id: newProduct.brand_id || '',
        images: newProduct.images || [],
        is_substituted: true,
        manualQty: {},
      };
    }));
    setShowSubstituteDialog(false);
    setSubstituteIdx(null);
    setSimilarProducts([]);
    toast.success(`Substituted with ${newProduct.design_code}`);
  };

  // Load pending SO items when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      api.get(`/sales-entries/pending-so-items/${selectedCustomer}`)
        .then(res => setPendingSOItems(res.data))
        .catch(() => setPendingSOItems([]));
    } else { setPendingSOItems([]); }
  }, [selectedCustomer]);

  // === Barcode / Design Code Scan ===
  const [qrScannerActive, setQrScannerActive] = useState(false);
  const qrScannerRef = useRef(null);

  const handleScanSubmit = (e) => {
    e.preventDefault();
    const code = scanInput.trim().toUpperCase();
    if (!code) return;
    // Try to parse as QR JSON
    try {
      const qrData = JSON.parse(scanInput.trim());
      if (qrData.sku && qrData.design) {
        handleQRScanResult(qrData);
        setScanInput('');
        if (scanRef.current) scanRef.current.focus();
        return;
      }
    } catch { /* not JSON — fall through to plain code lookup below */ }
    const product = products.find(p => p.design_code?.toUpperCase() === code || p.sku_prefix?.toUpperCase() === code);
    if (product) {
      addProductToEntry(product);
      setScanInput('');
      if (scanRef.current) scanRef.current.focus();
    } else {
      toast.error(`Product "${code}" not found`);
    }
  };

  const handleQRScanResult = (qrData) => {
    // qrData: { sku, design, size, color, wsp, pid }
    const product = products.find(p => p.id === qrData.pid || p.design_code?.toUpperCase() === qrData.design?.toUpperCase());
    if (!product) { toast.error(`Product "${qrData.design}" not found`); return; }
    const existingIdx = entryItems.findIndex(i => i.product_id === product.id);
    if (existingIdx >= 0) {
      // Product already in list, increment qty for this size/color
      const key = `${qrData.color}_${qrData.size}`;
      setEntryItems(prev => {
        const updated = [...prev];
        const item = { ...updated[existingIdx] };
        item.manualQty = { ...item.manualQty, [key]: (item.manualQty[key] || 0) + 1 };
        updated[existingIdx] = item;
        runAutoDetection(updated);
        return updated;
      });
      toast.success(`+1 ${product.design_code} (${qrData.size}/${qrData.color})`);
    } else {
      // Add new product with qty 1 for the scanned variant
      const key = `${qrData.color}_${qrData.size}`;
      setEntryItems(prev => [...prev, {
        product_id: product.id, design_code: product.design_code,
        brand_id: product.brand_id || '', images: product.images || [],
        variants: [], manualQty: { [key]: 1 },
      }]);
      toast.success(`Added ${product.design_code} (${qrData.size}/${qrData.color}) from QR`);
    }
  };

  const startQRScanner = async () => {
    setQrScannerActive(true);
    // Dynamically import to avoid SSR issues
    const { Html5Qrcode } = await import('html5-qrcode');
    setTimeout(() => {
      const scanner = new Html5Qrcode("qr-reader");
      qrScannerRef.current = scanner;
      scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          try {
            const qrData = JSON.parse(decodedText);
            if (qrData.sku && qrData.design) {
              handleQRScanResult(qrData);
            }
          } catch {
            // Not a QR JSON, try as design code
            const product = products.find(p => p.design_code?.toUpperCase() === decodedText.trim().toUpperCase());
            if (product) addProductToEntry(product);
            else toast.error(`Unknown code: ${decodedText}`);
          }
        },
        () => {} // ignore errors
      ).catch(err => { toast.error('Camera access denied'); setQrScannerActive(false); });
    }, 100);
  };

  const stopQRScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop().then(() => qrScannerRef.current.clear()).catch(() => {});
      qrScannerRef.current = null;
    }
    setQrScannerActive(false);
  };

  const addProductToEntry = (product) => {
    const exists = entryItems.find(i => i.product_id === product.id);
    if (exists) { toast.info('Product already added. Update quantities.'); return; }
    setEntryItems(prev => [...prev, {
      product_id: product.id, design_code: product.design_code,
      brand_id: product.brand_id || '', images: product.images || [],
      variants: [], manualQty: {},
    }]);
  };

  const updateEntryQty = (idx, color, size, qty) => {
    setEntryItems(prev => {
      const updated = prev.map((item, i) => {
        if (i !== idx) return item;
        const key = `${color}_${size}`;
        const newManual = { ...item.manualQty, [key]: Math.max(0, parseInt(qty) || 0) };
        return { ...item, manualQty: newManual };
      });
      // Run auto-detection
      runAutoDetection(updated);
      return updated;
    });
  };

  const removeEntryItem = (idx) => {
    setEntryItems(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      runAutoDetection(updated);
      return updated;
    });
  };

  const runAutoDetection = (items) => {
    const detected = [];
    for (let idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      const matchingSO = pendingSOItems.filter(si => si.design_code === item.design_code || si.product_id === item.product_id);
      if (matchingSO.length > 0) {
        for (const soItem of matchingSO) {
          for (const sov of soItem.variants) {
            const key = `${sov.color}_${sov.size}`;
            const entryQty = item.manualQty[key] || 0;
            if (entryQty > 0) {
              const autoQty = Math.min(entryQty, sov.pending_qty);
              detected.push({
                entryItemIdx: idx, design_code: item.design_code,
                color: sov.color, size: sov.size, autoQty,
                so_id: soItem.so_id, so_number: soItem.so_number,
                so_date: soItem.so_date, occasion: soItem.occasion,
                item_id: soItem.item_id,
              });
            }
          }
        }
      }
    }
    setAutoDetected(detected);
  };

  const getProductColors = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product?.skus) return [];
    return [...new Set(product.skus.map(s => s.color))];
  };

  const getProductSizes = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product?.skus) return [];
    return [...new Set(product.skus.map(s => s.size))];
  };

  const getAvailableStock = (productId, color, size) => {
    const product = products.find(p => p.id === productId);
    if (!product?.skus) return 0;
    const sku = product.skus.find(s => s.color === color && s.size === size);
    return sku ? (sku.current_stock || 0) - (sku.reserved_stock || 0) : 0;
  };

  const submitEntry = async () => {
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    if (entryItems.length === 0) { toast.error('Add at least one product'); return; }
    const hasQty = entryItems.some(item => Object.values(item.manualQty).some(q => q > 0));
    if (!hasQty) { toast.error('Enter quantities'); return; }

    // Build items with variant data and SO links
    const itemsPayload = entryItems.map((item, idx) => {
      const variants = [];
      const product = products.find(p => p.id === item.product_id);
      const wsp = product?.base_price || 0;
      for (const [key, qty] of Object.entries(item.manualQty)) {
        if (qty <= 0) continue;
        const [color, size] = key.split('_');
        const autoMatch = autoDetected.find(d => d.entryItemIdx === idx && d.color === color && d.size === size);
        variants.push({
          color, size, qty, wsp,
          linked_so_id: autoMatch?.so_id || null,
          linked_so_item_id: autoMatch?.item_id || null,
        });
      }
      return {
        product_id: item.product_id, design_code: item.design_code,
        brand_id: item.brand_id, variants,
        linked_so_id: variants.find(v => v.linked_so_id)?.linked_so_id || null,
        linked_so_item_id: variants.find(v => v.linked_so_item_id)?.linked_so_item_id || null,
        original_product_id: item.original_product_id || null,
        original_design_code: item.original_design_code || null,
      };
    }).filter(i => i.variants.length > 0);

    try {
      const payload = {
        customer_id: selectedCustomer,
        entry_type: autoDetected.length > 0 ? 'against_so' : entryType,
        linked_so_id: autoDetected[0]?.so_id || null,
        invoice_number: invoiceNumber,
        transport_name: transportName,
        order_source: orderSource || null,
        items: itemsPayload,
        notes: entryNotes,
      };
      const res = await api.post('/sales-entries', payload);
      toast.success(`Sales Entry ${res.data.entry_number} created! Stock deducted.`);
      if (autoDetected.length > 0) {
        toast.success(`Auto-linked ${autoDetected.length} variant(s) from pending SO(s)!`);
      }
      resetForm(); setIsCreateOpen(false); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };
  submitEntryRef.current = submitEntry;

  const resetForm = () => {
    setSelectedCustomer(''); setEntryType('direct_sale'); setTransportName('');
    setInvoiceNumber(''); setEntryItems([]); setEntryNotes(''); setOrderSource('');
    setPendingSOItems([]); setAutoDetected([]); setScanInput('');
  };

  const calcTotalQty = () => entryItems.reduce((s, item) => s + Object.values(item.manualQty).reduce((vs, q) => vs + q, 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="se-management-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Sales Entry</h1>
          <p className="text-gray-600 mt-1">Scan, pack, and dispatch — stock deducts here</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="se-filters">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[180px]">
              <Label className="text-xs mb-1 block">Search Entry</Label>
              <Input value={searchSE} onChange={e => handleSearchChange(e.target.value)} placeholder="Search entry#, customer, design, city, state, agent..." className="h-9 text-sm" data-testid="search-se-input" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Sort By</Label>
              <SortDropdown value={sortBy} onChange={setSortBy} options={seSortOptions} testId="se-sort" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs mb-1 block">Customer</Label>
              <MultiSelectFilter label="All Customers" options={custFilterOptions} selected={filterCustomers} onChange={setFilterCustomers} />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">Type</Label>
              <MultiSelectFilter label="All Types" options={entryTypeOptions} selected={filterEntryTypes} onChange={setFilterEntryTypes} />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">Tally Status</Label>
              <MultiSelectFilter label="All Tally" options={tallyFilterOptions} selected={filterTallyStatuses} onChange={setFilterTallyStatuses} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Order Source</Label>
              <MultiSelectFilter label="All Sources" options={orderSourceOptions} selected={filterOrderSources} onChange={setFilterOrderSources} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Agent</Label>
              <MultiSelectFilter label="All Agents" options={agentFilterOptions} selected={filterAgents} onChange={setFilterAgents} />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Distributor</Label>
              <MultiSelectFilter label="All Distributors" options={distFilterOptions} selected={filterDistributors} onChange={setFilterDistributors} />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">City</Label>
              <MultiSelectFilter label="All Cities" options={cityFilterOptions} selected={filterCities} onChange={setFilterCities} />
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs mb-1 block">State</Label>
              <MultiSelectFilter label="All States" options={stateFilterOptions} selected={filterStates} onChange={setFilterStates} />
            </div>
            <div className="min-w-[140px]">
              <Label className="text-xs mb-1 block">Payment Mode</Label>
              <MultiSelectFilter label="All Payments" options={[
                { value: 'advance', label: 'Advance' },
                { value: 'credit', label: 'Credit' },
                { value: 'cod', label: 'COD' },
                { value: 'partial_advance', label: 'Partial Advance' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'online', label: 'Online' },
              ]} selected={filterPaymentModes} onChange={setFilterPaymentModes} testId="se-filter-payment-mode" />
            </div>
            <div className="min-w-[130px]">
              <Label className="text-xs mb-1 block">Staff</Label>
              <MultiSelectFilter label="All Staff" options={staffFilterOptions} selected={filterStaff} onChange={setFilterStaff} testId="se-filter-staff" />
            </div>
            {hasAnyFilter && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => {
                setFilterCustomers([]); setFilterStatuses([]); setFilterTallyStatuses([]); setFilterEntryTypes([]);
                setFilterOrderSources([]); setFilterCities([]); setFilterStates([]); setFilterAgents([]); setFilterDistributors([]); setFilterPaymentModes([]); setFilterStaff([]); setSearchSE('');
              }}>Clear All</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entry List */}
      <Card data-testid="se-list">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Sales Entries ({totalCount})</CardTitle>
            {canManage && (
              <Button size="sm" onClick={() => navigate('/create-sales-entry')} className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 shadow-sm" data-testid="create-se-button">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                New Sales Entry
              </Button>
            )}
          </div>
          <CardDescription>All dispatched goods with stock deductions</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Drafts Section */}
          {drafts.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="se-drafts-section">
              <p className="text-xs font-semibold text-amber-800 mb-2">Drafts ({drafts.length})</p>
              <div className="flex flex-wrap gap-2">
                {drafts.map(d => (
                  <div key={d.id} className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-sm">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                    <button className="font-medium text-amber-900 hover:text-blue-700 hover:underline"
                      onClick={() => navigate('/create-sales-entry', { state: { draftId: d.id } })}
                      data-testid={`se-draft-${d.id}`}>
                      {d.title || 'Untitled Draft'}
                    </button>
                    <span className="text-xs text-gray-400">{new Date(d.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    <button className="text-gray-400 hover:text-red-500 text-xs ml-1" onClick={async () => {
                      await api.delete(`/drafts/${d.id}`);
                      setDrafts(prev => prev.filter(x => x.id !== d.id));
                    }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {filteredEntries.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sales Entries</h3>
              <p className="text-gray-600">Create your first sales entry to start dispatching.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="se-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) { bulk.selectAll(filteredEntries.map(x => x.id)); } else { bulk.clearSelection(); }}} data-testid="se-select-all" /></th>
                    <th className="px-4 py-3 text-left font-medium">SE #</th>
                    <th className="px-4 py-3 text-left font-medium">Invoice #</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Customer</th>
                    <th className="px-4 py-3 text-left font-medium">Agent</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Qty</th>
                    <th className="px-4 py-3 text-right font-medium">Amount</th>
                    <th className="px-4 py-3 text-center font-medium">Type</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredEntries.map(entry => (
                    <tr key={entry.id} className={`hover:bg-emerald-50/50 cursor-pointer transition-colors group ${bulk.isSelected(entry.id) ? 'bg-blue-50' : ''}`} onClick={() => navigate(`/sales-entry/${entry.id}`)} data-testid={`se-row-${entry.id}`}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(entry.id)} onChange={() => bulk.toggleSelect(entry.id)} data-testid={`se-select-${entry.id}`} /></td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-emerald-700 font-mono group-hover:underline" data-testid={`se-number-link-${entry.id}`}>{entry.entry_number}</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-blue-700" data-testid={`se-invoice-${entry.id}`}>{entry.invoice_number || <span className="text-gray-400">-</span>}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {fmtDate(entry.entry_date)}
                        {entry.created_at && <span className="block text-[10px] text-gray-400">{new Date(entry.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px]">
                        <div className="truncate">{getCustName(entry.customer_id)}</div>
                        {entry.event_name && (
                          <span
                            className="inline-block mt-1 text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200 px-1.5 py-0.5 rounded truncate max-w-full"
                            title={entry.event_name}
                            data-testid={`se-row-event-${entry.id}`}
                          >
                            🎟 {entry.event_name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-indigo-700 font-medium truncate max-w-[120px]" data-testid={`se-agent-${entry.id}`}>{entry.agent_name || '-'}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{entry.items?.length || 0}</td>
                      <td className="px-4 py-3 text-right font-medium">{entry.total_qty || 0}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">₹{(entry.total_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <Badge className={entry.entry_type === 'against_so' ? 'bg-amber-100 text-amber-700 text-[11px]' : 'bg-emerald-100 text-emerald-700 text-[11px]'}>
                            {entry.entry_type === 'against_so' ? 'Against SO' : 'Direct'}
                          </Badge>
                          <Badge className={entry.bill_type === 'challan' ? 'bg-orange-100 text-orange-700 text-[10px]' : 'bg-blue-100 text-blue-700 text-[10px]'}>
                            {entry.bill_type === 'challan' ? 'Challan' : 'Invoice'}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        {canChangeTally ? (
                          <select
                            value={entry.tally_status || 'pending'}
                            onChange={e => handleStatusChange(entry.id, e.target.value)}
                            className={`text-xs font-medium px-2 py-1 rounded-md border cursor-pointer ${TALLY_STATUSES[entry.tally_status || 'pending']?.color || 'bg-gray-100 text-gray-700'}`}
                            data-testid={`tally-status-${entry.id}`}
                          >
                            <option value="pending">Bill Pending</option>
                            <option value="billed">Billed</option>
                            {canSetAdminHold && <option value="hold_admin">Admin Hold</option>}
                            <option value="hold_sales">Sales Hold</option>
                          </select>
                        ) : (
                          <Badge className={`text-xs ${TALLY_STATUSES[entry.tally_status || 'pending']?.color || 'bg-gray-100 text-gray-700'}`} data-testid={`tally-badge-${entry.id}`}>
                            {TALLY_STATUSES[entry.tally_status || 'pending']?.label || 'Bill Pending'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{entry.created_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PaginationBar currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={goToPage} testIdPrefix="se-page" />


      {/* Substitution Dialog */}
      {showSubstituteDialog && (
        <Dialog open={showSubstituteDialog} onOpenChange={() => { setShowSubstituteDialog(false); setSubstituteIdx(null); }}>
          <DialogContent className="max-w-2xl z-[60] max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Substitute Product</DialogTitle>
              <DialogDescription>
                {substituteIdx !== null && entryItems[substituteIdx] && (
                  <>Replacing <strong>Design #{entryItems[substituteIdx].design_code}</strong> — select a similar product below</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <Input
                placeholder="Search by design code..."
                value={substituteSearch}
                onChange={e => setSubstituteSearch(e.target.value)}
                data-testid="substitute-search"
              />
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(substituteSearch
                  ? products.filter(p => p.design_code?.toLowerCase().includes(substituteSearch.toLowerCase()) && p.id !== entryItems[substituteIdx]?.product_id)
                  : similarProducts
                ).map(p => {
                  const totalStock = (p.skus || []).reduce((s, sk) => s + (sk.current_stock || 0) - (sk.reserved_stock || 0), 0);
                  return (
                    <div key={p.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-emerald-50 hover:border-emerald-400 transition-colors cursor-pointer"
                      onClick={() => confirmSubstitution(p)} data-testid={`substitute-product-${p.id}`}>
                      {p.images?.[0] ? (
                        <img src={`${API_BASE}${p.images[0]}`} alt={p.design_code} className="w-14 h-14 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-bold text-gray-500">{p.design_code}</div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{p.design_code}</span>
                          {p.brand_id && <Badge className="text-xs bg-indigo-100 text-indigo-700">{getBrandName(p.brand_id)}</Badge>}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{p.category || ''} {p.fabric ? `| ${p.fabric}` : ''}</p>
                        <p className="text-xs mt-0.5">
                          Stock: <span className={`font-bold ${totalStock > 0 ? 'text-green-600' : 'text-red-500'}`}>{totalStock} pcs</span>
                          {p.base_price ? <span className="ml-2 text-gray-600">WSP: ₹{p.base_price}</span> : null}
                        </p>
                      </div>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Select</Button>
                    </div>
                  );
                })}
                {(substituteSearch ? products.filter(p => p.design_code?.toLowerCase().includes(substituteSearch.toLowerCase())) : similarProducts).length === 0 && (
                  <p className="text-center text-gray-500 py-6">No similar products found. Try searching by design code.</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Share Dialog */}
      {shareDialogEntry && (
        <Dialog open={!!shareDialogEntry} onOpenChange={() => setShareDialogEntry(null)}>
          <DialogContent className="max-w-sm z-50">
            <DialogHeader>
              <DialogTitle>Share {shareDialogEntry.entry_number}</DialogTitle>
              <DialogDescription>Choose format and sharing method</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Format</Label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => { setShareFormat('pdf'); }} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'pdf' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 hover:border-emerald-300'}`} data-testid="se-share-format-pdf">
                    <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    <span className="text-sm font-medium">PDF</span>
                  </button>
                  <button type="button" onClick={() => { setShareFormat('excel'); setHideColor(false); }} className={`flex-1 p-3 rounded-lg border-2 text-center transition-colors ${shareFormat === 'excel' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-green-300'}`} data-testid="se-share-format-excel">
                    <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <span className="text-sm font-medium">Excel</span>
                  </button>
                </div>
              </div>
              {shareFormat === 'pdf' && (
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="se-hide-color-option">
                  <input type="checkbox" id="seHideColor" checked={hideColor} onChange={e => setHideColor(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" data-testid="se-hide-color-checkbox" />
                  <label htmlFor="seHideColor" className="text-sm font-medium text-emerald-800 cursor-pointer">Hide Color Column</label>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Share via</Label>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => executeShare(shareDialogEntry, shareFormat, 'download')} className="justify-start bg-emerald-600 hover:bg-emerald-700" data-testid="se-share-action-download">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Download {shareFormat.toUpperCase()}
                  </Button>
                  <Button variant="outline" className="justify-start text-green-700 border-green-300 hover:bg-green-50" onClick={() => executeShare(shareDialogEntry, shareFormat, 'whatsapp')} data-testid="se-share-action-whatsapp">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </Button>
                  <Button variant="outline" className="justify-start text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => executeShare(shareDialogEntry, shareFormat, 'email')} data-testid="se-share-action-email">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                    Email
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Invoice Number Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={v => { if (!v) setBillingDialog(null); }}>
        <DialogContent className="max-w-sm z-50">
          <DialogHeader>
            <DialogTitle>Enter Invoice Number</DialogTitle>
            <DialogDescription>Optional: Enter the invoice number for this sales entry</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Invoice Number</Label>
              <Input
                value={billingDialog?.inputValue || ''}
                onChange={e => setBillingDialog(prev => ({ ...prev, inputValue: e.target.value }))}
                placeholder="e.g. INV-001"
                autoFocus
                data-testid="billing-invoice-input"
                onKeyDown={e => { if (e.key === 'Enter') confirmBilling(); }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setBillingDialog(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={confirmBilling} data-testid="billing-confirm-btn">Mark as Billed</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="SE" label="sales entries" onComplete={() => { bulk.clearSelection(); fetchEntries(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default SalesEntryManagement;
